import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetDataParam} from 'features/core/shared/widget/widget-data-param';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {Sorting} from 'features/core/shared/misc/sorting';
import {DtoImportObjectContext} from 'features/core/shared/dto/dto.import.object.context';
import {DtoImportObjectContextEntry} from 'features/core/shared/dto/dto.import.object.context.entry';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from 'features/core/shared/helper/widget-function-call-back-create';
import {ObjectIdentifier} from 'features/core/shared/basic/object-identifier';
import {ObjectIdentifierData} from 'features/core/shared/basic/object-identifier-data';
import {ProvisionEventDeterminationType} from '../../../../../enum/provision-event-determination-type.enum';
import {
    CpProvisionEventDeterminationTypeMap,
    CpProvisionTypeLookupService
} from '../../../../../service/ql/cp-provision-type-lookup.service';

/**
 * navItem "Provisionsereignisse" of the ProvisionType object component — the two-level event
 * family (04-frontend.md §6, entries 4 and 4a; decision AX-13 = A).
 *
 * ## Why a selectable master-detail is correct HERE
 * `cp.ProvisionTypeContractEvent` (K-15) is a child of `cp.ProvisionType` and owns exactly ONE
 * same-module configuration child of its own: `cp.ProvisionTypeContractEventPriceReason` (K-16,
 * mandatory FK `provisionTypeContractEventDtoId`). One child means SIMPLE under the Complex
 * Configuration Entity Rule, and for simple entities the selectable-parent + FK-filtered-child
 * pattern is the prescribed form (`fe_core_page`, decision matrix "0 or 1 children"). 04 §6 lists
 * K-16 as level 4a underneath K-15 for exactly that reason, and assigns K-15 the `.selectable`
 * variant "weil zweistufig".
 *
 * The in-module precedent for the mechanics is the sibling navItem "Matrizen"
 * (`../cp-provision-type-matrix/`), which solves the identical two-level shape for
 * `cp.ProvisionTypeMatrix` -> `cp.ProvisionTypeMatrixAgent`; this component is built the same way
 * so both two-level points of the object page behave alike.
 *
 * ## The visibility rule of level 2 (04 §6, form rules of K-15)
 * "`determinationType` steuert die Sichtbarkeit der zweiten Ebene 4a (nur bei
 * `CONTRACT_PRICE_CHANGE`)." A `CONTRACT_START` event fires at the start of the contract and never
 * reads a price reason, so its price-reason list is not shown at all — instead the panel explains
 * why. The determination types come from {@link CpProvisionTypeLookupService}, which loads
 * them for all events of the provision type in one query when the tab opens.
 *
 * An UNKNOWN determination type (probe failed, unrecognised value) shows the level rather than
 * hiding it: a failed lookup must never make existing configuration unreachable.
 *
 * ## FK pre-fill of level 2 (CONFIG-012)
 * `cp.ProvisionTypeContractEventPriceReason` carries TWO mandatory FKs — the owning event and the
 * denormalised `provisionType` that makes the business key (provisionType, priceReason) a database
 * unique index. Both are pre-filled and read-only on create, and both are put into the
 * `DtoImportObjectContext`, so the create form can never produce the one state the write path
 * rejects: a price reason whose `provisionType` disagrees with its event's.
 *
 * ## State model (CONFIG-011)
 * Both widget aliases stay constant across every selection, so the user's column widths and
 * sorting survive a selection change. Without an event selected the level-2 widget is not built at
 * all and the right-hand side only shows the hint to pick an event.
 */
@Component({
    selector: 'cp-provision-type-contract-event',
    templateUrl: './cp-provision-type-contract-event.component.html',
    styleUrls: ['./cp-provision-type-contract-event.component.scss'],
    standalone: false
})
export class CpProvisionTypeContractEventComponent implements OnInit, OnChanges, OnDestroy {

    /** Stable widget aliases (CONFIG-011) — identical in every selection state. */
    private static readonly cAliasEvent = 'config.cp.ProvisionTypeContractEvent.selectable';
    private static readonly cAliasPriceReason = 'config.cp.ProvisionTypeContractEventPriceReason.table';

    busy: boolean;
    initialized: boolean;

    @Input() provisionTypeId: number;

    /** Selected provision event, `null` = nothing selected, level 2 stays empty. */
    selectedEventId: number | null = null;

    /**
     * Determination type of the selected event; `null` while unknown. Only
     * `CONTRACT_PRICE_CHANGE` and `null` build the price-reason level.
     */
    selectedDeterminationType: ProvisionEventDeterminationType | null = null;

    eventWidget: WidgetData;
    priceReasonWidget: WidgetData;

    eventImportContext: DtoImportObjectContext;
    priceReasonImportContext: DtoImportObjectContext;

    /** Determination types of every event of this provision type, loaded once per tab. */
    private determinationTypes: CpProvisionEventDeterminationTypeMap = {};

    private determinationSubscription: Subscription;

    /** Exposed for the template, which must not reference enum members by literal. */
    readonly cContractStart = ProvisionEventDeterminationType.CONTRACT_START;

    constructor(private lookupService: CpProvisionTypeLookupService) {
    }

    ngOnInit(): void {
        this.initComponent();
    }

    initComponent(): void {
        this.refreshComponent();
    }

    refreshComponent(): void {

        this.cancelDeterminationLoad();

        if (!this.provisionTypeId) {
            this.eventWidget = null;
            this.priceReasonWidget = null;
            this.eventImportContext = null;
            this.priceReasonImportContext = null;
            this.determinationTypes = {};
            this.initialized = true;
            return;
        }

        this.refreshEventWidget();
        this.refreshPriceReasonWidget();
        this.loadDeterminationTypes();

        this.initialized = true;
    }

    /** Level 1 — the provision events of this provision type. */
    private refreshEventWidget(): void {

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Selectable list of the provision events (cp.ProvisionTypeContractEvent) of
         *     EXACTLY this provision type. The selection drives the price-reason list below; a
         *     new row gets its mandatory FK provisionType pre-filled.
         */
        this.eventWidget = WidgetFactory.createWidgetSelectableEntityQl(
            CpProvisionTypeContractEventComponent.cAliasEvent,
            'Provisionsereignisse',
            'cp.ProvisionTypeContractEvent',
            'Keine Provisionsereignisse vorhanden',
            [FilterCriteria.create('e.provisionTypeId', FilterCriteria.cOperatorEqual, this.provisionTypeId)],
            [new Sorting('e.name', true)],
            WidgetDataParam.create('attributeToDisplay', 'e.name'),
            WidgetDataParam.create('showSearch', true)
        );

        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeDtoId', fieldValue: this.provisionTypeId}
        ];
        this.eventWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.eventImportContext = new DtoImportObjectContext(
            [new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId)]);
    }

    /**
     * Level 2 — the price reasons (K-16) that let a contract price time slice trigger the SELECTED
     * event.
     *
     * Built only for a selected event whose determination type is `CONTRACT_PRICE_CHANGE` or still
     * unknown (04 §6). For a `CONTRACT_START` event the template explains the absence instead of
     * showing an empty table the user could fill with rows the engine never reads.
     */
    private refreshPriceReasonWidget(): void {

        const eventId = this.selectedEventId;

        if (!eventId || this.selectedDeterminationType === this.cContractStart) {
            this.priceReasonWidget = null;
            this.priceReasonImportContext = null;
            return;
        }

        /**
         * AI:
         * Status: "in progress"
         * Type: Widget
         * SubType: Configuration
         * Reason: Price reasons (cp.ProvisionTypeContractEventPriceReason) of the SELECTED
         *     provision event — the cm.PriceReason values whose price time slice triggers this
         *     event. A new row gets BOTH mandatory FKs pre-filled: the owning event and the
         *     denormalised provision type of the business key.
         */
        this.priceReasonWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CpProvisionTypeContractEventComponent.cAliasPriceReason,
            'Preisgründe des gewählten Ereignisses',
            'cp.ProvisionTypeContractEventPriceReason',
            [FilterCriteria.create('e.provisionTypeContractEventId', FilterCriteria.cOperatorEqual, eventId)],
            [new Sorting('e.id', false)],
            WidgetDataParam.create('defaultFormFields', [
                'priceReasonDtoName',
                'entityStatus'
            ])
        );

        // Both FKs are mandatory, so both are pre-filled and read-only here (CONFIG-012): the
        // owning event and the denormalised provision type the unique business key needs.
        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'provisionTypeContractEventDtoId', fieldValue: eventId},
            {fieldName: 'provisionTypeDtoId', fieldValue: this.provisionTypeId}
        ];
        this.priceReasonWidget.functionCallbacks =
            WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        this.priceReasonImportContext = new DtoImportObjectContext([
            new DtoImportObjectContextEntry('cp.ProvisionTypeContractEvent', eventId),
            new DtoImportObjectContextEntry('cp.ProvisionType', this.provisionTypeId)
        ]);
    }

    /**
     * Load the determination types of all events of this provision type once, so a selection can
     * decide about level 2 without a further round trip.
     */
    private loadDeterminationTypes(): void {

        this.determinationSubscription = this.lookupService
            .getEventDeterminationTypes(this.provisionTypeId)
            .subscribe(types => {
                this.determinationTypes = types ?? {};
                // A selection made while the lookup was still running is re-evaluated now.
                if (this.selectedEventId) {
                    this.selectedDeterminationType = this.resolveDeterminationType(this.selectedEventId);
                    this.refreshPriceReasonWidget();
                }
            });
    }

    onEventSelect(event: ObjectIdentifier | ObjectIdentifierData): void {
        const eventId = this.resolveObjectId(event);
        if (!eventId) {
            return;
        }
        this.selectedEventId = eventId;
        this.selectedDeterminationType = this.resolveDeterminationType(eventId);
        this.refreshPriceReasonWidget();
    }

    onEventDeselect(): void {
        this.selectedEventId = null;
        this.selectedDeterminationType = null;
        this.refreshPriceReasonWidget();
    }

    /**
     * Determination type of one event.
     *
     * Prefers the freshly loaded map; falls back to `null` = unknown, which keeps level 2 visible.
     * That direction of the fallback is deliberate: hiding existing price reasons because a lookup
     * failed would be the worse error.
     */
    private resolveDeterminationType(eventId: number): ProvisionEventDeterminationType | null {
        const value = this.determinationTypes ? this.determinationTypes[eventId] : undefined;
        return value === undefined ? null : value;
    }

    /** Robust id extraction — selectable and table widgets fill `objectId`, QL rows `e.id`. */
    private resolveObjectId(event: ObjectIdentifier | ObjectIdentifierData): number | null {
        if (!event) {
            return null;
        }
        if (event.objectId) {
            return +event.objectId;
        }
        const data = (event as ObjectIdentifierData).data;
        return data && data['e.id'] ? +data['e.id'] : null;
    }

    private cancelDeterminationLoad(): void {
        if (this.determinationSubscription) {
            this.determinationSubscription.unsubscribe();
            this.determinationSubscription = null;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        if (changes['provisionTypeId']) {
            this.selectedEventId = null;
            this.selectedDeterminationType = null;
            this.refreshComponent();
        }
    }

    ngOnDestroy(): void {
        this.cancelDeterminationLoad();
    }

}
